import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.css'
})
export class PaginationComponent {
  @Input() page = 1;
  @Input() pageSize = 5;
  @Input() totalItems = 0;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  // Exponer Math para usarlo en la plantilla
  Math = Math;

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  /**
   * Navega a la página especificada
   * @param page Número de página al que navegar
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.pageChange.emit(page);
    }
  }

  /**
   * Cambia el tamaño de página
   * @param size Nuevo tamaño de página
   */
  changePageSize(size: number): void {
    this.pageSizeChange.emit(size);
  }

  /**
   * Genera un array con los números de página visibles
   * Usa -1 como marcador para los puntos suspensivos
   * @returns Array de números de página a mostrar
   */
  getVisiblePages(): number[] {
    const totalPages = this.totalPages;
    const currentPage = this.page;
    const maxVisiblePages = 5; // Número máximo de páginas a mostrar
    
    // Si hay pocas páginas, mostrarlas todas
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    // Siempre mostrar la primera y última página
    const pages: number[] = [];
    
    // Añadir primera página
    pages.push(1);
    
    // Calcular rango de páginas alrededor de la página actual
    const leftOffset = Math.floor((maxVisiblePages - 2) / 2);
    const rightOffset = Math.ceil((maxVisiblePages - 2) / 2);
    
    let startPage = Math.max(2, currentPage - leftOffset);
    let endPage = Math.min(totalPages - 1, currentPage + rightOffset);
    
    // Ajustar si estamos cerca del inicio o fin
    if (currentPage - leftOffset < 2) {
      endPage = Math.min(totalPages - 1, maxVisiblePages - 1);
    }
    
    if (currentPage + rightOffset > totalPages - 1) {
      startPage = Math.max(2, totalPages - maxVisiblePages + 1);
    }
    
    // Añadir puntos suspensivos a la izquierda si es necesario
    if (startPage > 2) {
      pages.push(-1); // -1 representa los puntos suspensivos
    }
    
    // Añadir páginas del rango calculado
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Añadir puntos suspensivos a la derecha si es necesario
    if (endPage < totalPages - 1) {
      pages.push(-1); // -1 representa los puntos suspensivos
    }
    
    // Añadir última página si no es la primera
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  }
}
